<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useFloating } from '@/composables/useFloating.js'
import { useI18n } from 'vue-i18n'
import { ENTITIES } from '@/lib/entities.js'
import { formatRelative } from '@/lib/format.js'
import { reportError } from '@/composables/useSave.js'
import { useUi } from '@/stores/ui.js'
import { addComment, apiField, changeComment, commentsOf, removeComment } from '@/lib/fieldComments.js'
import UiButton from '@/components/ui/UiButton.vue'
import RichTextEditor from '@/components/form/RichTextEditor.vue'
import RichText from '@/components/record/RichText.vue'

/**
 * The comments on one field of a record (D-135): a speech bubble by the field's label — with
 * the number of open comments when there are any — opening a small window to read, add,
 * resolve or remove them. Shared with everyone who sees the record, or kept to oneself.
 */
const props = defineProps({ entity: { type: String, required: true }, uuid: { type: String, required: true }, field: { type: String, required: true }, label: { type: String, default: '' } })
const { t } = useI18n()
const ui = useUi()
const doc = ref(null)
const editorKey = ref(0)
const isPrivate = ref(false)
const busy = ref(false)
// The window floats over the page by its bubble (D-152). A press in another dialog (a
// confirmation) or in the mention list keeps it open.
const { open, anchor: root, floating: pop, style: popStyle, toggle, show } = useFloating({ inside: (target) => Boolean(target.closest?.('[role=dialog], [data-mention-list]')) })

const supported = computed(() => Boolean(ENTITIES[props.entity]))
const state = computed(() => (supported.value ? commentsOf(props.entity, props.uuid) : { list: [] }))
const mine = computed(() => state.value.list.filter((c) => c.field === apiField(props.field)))
// The thread a notification's "Reply" points at (?comments=<field>, D-257): opened, in sight.
const route = useRoute()
onMounted(async () => {
  if (route.query.comments !== apiField(props.field)) return
  await nextTick()
  root.value?.scrollIntoView?.({ block: 'center' })
  show()
})
const unresolved = computed(() => mine.value.filter((c) => !c.resolved_at).length)

// Something written: text or a mention.
const hasText = computed(() => JSON.stringify(doc.value || {}).match(/"text":"\s*[^"\s]|"type":"mention"/) !== null)
async function add() {
  if (!hasText.value) return
  busy.value = true
  try {
    await addComment(props.entity, props.uuid, apiField(props.field), doc.value, isPrivate.value, props.label)
    doc.value = null
    editorKey.value++
    isPrivate.value = false
  } catch (e) {
    reportError(e)
  } finally {
    busy.value = false
  }
}
async function toggleResolved(c) {
  try {
    await changeComment(props.entity, props.uuid, c, { resolved: !c.resolved_at })
  } catch (e) {
    reportError(e)
  }
}
async function remove(c) {
  if (!(await ui.ask({ title: t('fieldComments.deleteTitle'), message: c.body, confirmLabel: t('common.delete'), danger: true }))) return
  try {
    await removeComment(props.entity, props.uuid, c)
  } catch (e) {
    reportError(e)
  }
}
// Opened from elsewhere too (the document's menu under the right mouse button, D-188).
defineExpose({ show })
</script>

<template>
  <span v-if="supported" ref="root" class="relative inline-flex">
    <button
      type="button"
      :class="['inline-flex items-center gap-0.5 rounded px-1 text-2xs transition-opacity', unresolved ? 'text-brand-600 dark:text-brand-300' : mine.length ? 'text-faint' : 'text-faint opacity-0 group-hover:opacity-100 focus:opacity-100', open ? 'opacity-100' : '']"
      :title="$t('fieldComments.title', { field: label })"
      :aria-label="$t('fieldComments.title', { field: label })"
      :aria-expanded="open"
      :data-testid="`field-comments-${field}`"
      @click.prevent.stop="toggle"
    >
      <i :class="['fa-comment', mine.length ? 'fa-solid' : 'fa-regular']" aria-hidden="true"></i>
      <span v-if="unresolved" class="tabular-nums">{{ unresolved }}</span>
    </button>
    <Teleport to="body">
    <div v-if="open" ref="pop" :style="popStyle" class="surface fixed w-80 max-w-[85vw] overflow-y-auto rounded-xl border border-default p-3 text-left font-normal normal-case tracking-normal text-[var(--text)] shadow-xl" role="dialog" :aria-label="$t('fieldComments.title', { field: label })" @click.stop>
      <p class="mb-2 text-xs font-semibold text-muted">{{ $t('fieldComments.title', { field: label }) }}</p>
      <ul v-if="mine.length" class="mb-2 max-h-64 space-y-2 overflow-y-auto">
        <li v-for="c in mine" :key="c.uuid" :class="['rounded-lg border border-default p-2 text-sm', c.resolved_at ? 'opacity-60' : '']" :data-comment="c.uuid">
          <div class="mb-0.5 flex items-center gap-1 text-2xs text-faint">
            <span class="font-medium text-muted">{{ c.author?.label }}</span>
            <span>· {{ formatRelative(c.created_at) }}</span>
            <i v-if="c.is_private" class="fa-solid fa-lock" :title="$t('fieldComments.private')" aria-hidden="true"></i>
            <span class="flex-1"></span>
            <UiButton size="xs" variant="ghost" :icon="c.resolved_at ? 'fa-rotate-left' : 'fa-check'" :title="c.resolved_at ? $t('fieldComments.reopen') : $t('fieldComments.resolve')" :aria-label="c.resolved_at ? $t('fieldComments.reopen') : $t('fieldComments.resolve')" @click="toggleResolved(c)" />
            <UiButton v-if="c.mine" size="xs" variant="ghost" icon="fa-trash-can" class="hover:text-red-600" :aria-label="$t('common.delete')" @click="remove(c)" />
          </div>
          <RichText v-if="c.body_doc" :class="['text-sm', c.resolved_at ? 'line-through decoration-1' : '']" :doc="c.body_doc" :fallback="c.body" />
          <p v-else :class="['whitespace-pre-line', c.resolved_at ? 'line-through decoration-1' : '']">{{ c.body }}</p>
          <p v-if="c.resolved_at" class="mt-0.5 text-2xs text-faint">{{ $t('fieldComments.resolvedBy', { name: c.resolved_by?.label || '', when: formatRelative(c.resolved_at) }) }}</p>
        </li>
      </ul>
      <p v-else class="mb-2 text-xs text-faint">{{ $t('fieldComments.none') }}</p>
      <div data-testid="field-comment-text"><RichTextEditor :key="editorKey" palette v-model="doc" :placeholder="$t('fieldComments.placeholder')" mentions compact min-height="3.5rem" @submit="add" /></div>
      <div class="mt-2 flex items-center gap-2">
        <label class="flex items-center gap-1 text-xs text-muted"><input v-model="isPrivate" type="checkbox" class="accent-brand-600" /> <i class="fa-solid fa-lock text-3xs" aria-hidden="true"></i> {{ $t('fieldComments.onlyMe') }}</label>
        <span class="flex-1"></span>
        <UiButton size="sm" variant="primary" :loading="busy" :disabled="!hasText" data-testid="field-comment-add" @click="add">{{ $t('fieldComments.add') }}</UiButton>
      </div>
    </div>
    </Teleport>
  </span>
</template>
